import type { PersonaId } from '@/demo/etat';

/**
 * LA VISITE GUIDÉE, déclarée ici : chapitres → étapes. Même grammaire que
 * `tourSteps.ts` de Plans d'actions, pour qu'un chapitre se relise de la même
 * façon d'un outil à l'autre.
 *
 * Une étape vise un élément par sélecteur (convention `data-tour="clé"`), peut
 * imposer une route (le moteur y navigue avant d'afficher la bulle) et un RÔLE
 * (le moteur fait endosser ce rôle dans la démo) : le circuit IGD passe de
 * main en main, la visite aussi. Sans cible, la bulle s'affiche au centre.
 *
 * Les chapitres se suivent dans l'ordre d'une déclaration : on la saisit, le
 * manager la valide, la RH la paie, l'administrateur tient les règles.
 */

export type Placement = 'haut' | 'bas' | 'auto';

/**
 * Avance automatique d'une étape « à vous de jouer » : dès que le geste
 * attendu est fait, la visite passe seule à la suite.
 *  - `route`        : l'utilisateur a changé d'écran (ouvert une déclaration…) ;
 *  - `apparait`     : un élément est apparu (un onglet ouvert…) ;
 *  - `modaleFermee` : une fenêtre ouverte pendant l'étape s'est refermée.
 */
export type Avance = { type: 'route' } | { type: 'apparait'; selecteur: string } | { type: 'modaleFermee' };

export interface Etape {
  cible?: string;
  route?: string;
  /** Rôle à endosser AVANT d'afficher l'étape. */
  persona?: PersonaId;
  titre: string;
  texte: string;
  placement?: Placement;
  /** Consigne « à vous de jouer », en encart. */
  consigne?: string;
  avance?: Avance;
}

export interface Chapitre {
  id: string;
  titre: string;
  description: string;
  icone: string;
  /** Le rôle dans lequel le chapitre se joue. */
  persona: PersonaId;
  etapes: Etape[];
}

export const CHAPITRES: Chapitre[] = [
  {
    id: 'prise-en-main',
    titre: 'Prise en main',
    description: 'Se repérer : le menu, le tableau de bord et ses chiffres, le profil.',
    icone: '🧭',
    persona: 'collaborateur',
    etapes: [
      {
        titre: 'Bienvenue dans le mode découverte',
        texte:
          'Vous êtes dans une entreprise fictive, avec ses collaborateurs, ses déclarations et ses périodes de paie. ' +
          'Tout ce que vous faites ici reste dans votre navigateur : rien n’atteint les vraies données, et personne ' +
          'd’autre ne le voit. Vous commencez dans la peau de Claire DURAND, collaboratrice.',
      },
      {
        cible: 'aside',
        route: '/',
        titre: 'Le menu',
        texte:
          'Il suit vos rôles : une collaboratrice voit son tableau de bord et ses déclarations ; un manager ajoute ' +
          '« À valider » ; la RH, l’export paie ; l’administrateur, l’administration.',
      },
      {
        cible: '[data-tour="nouvelle-declaration"]',
        placement: 'bas',
        titre: 'Le geste le plus fréquent',
        texte: 'Déclarer un déplacement est toujours à portée de main, en haut de chaque écran — y compris sur téléphone.',
      },
      {
        cible: '[data-tour="kpi"]',
        route: '/',
        titre: 'Vos chiffres de la période',
        texte:
          'Déclarations, indemnités de grand déplacement, nuitées et total : une couleur par nature de chiffre. ' +
          'Seul ce qui est validé ou exporté est compté — un brouillon ne paie rien.',
      },
      {
        cible: '[data-tour="compteurs"]',
        titre: 'Où en sont vos déclarations',
        texte: 'Une pastille par statut : combien, et pour quel montant. Chacune ouvre la liste filtrée sur ce statut.',
        consigne: 'Cliquez sur « À corriger » : une déclaration vous a été renvoyée.',
        avance: { type: 'route' },
      },
      {
        cible: '[data-tour="liste-declarations"]',
        titre: 'La liste, déjà filtrée',
        texte:
          'Le statut vient de la pastille ; la période et la recherche affinent. Une ligne s’ouvre d’un clic, avec ' +
          'son historique complet — le commentaire du manager y est.',
      },
      {
        cible: '[data-tour="profil"]',
        placement: 'bas',
        titre: 'Votre profil',
        texte:
          'Vos rôles, votre société, le thème clair ou sombre — et surtout votre adresse de départ par défaut : ' +
          'elle pré-remplit chaque nouvelle déclaration.',
      },
      {
        titre: 'À vous de jouer',
        texte:
          'Le bandeau du bas vous suit partout : il rappelle que vous êtes en démo, change de rôle, remet la démo à ' +
          'zéro ou vous en fait sortir. Chapitre suivant : déclarer un déplacement.',
      },
    ],
  },
  {
    id: 'declarer',
    titre: 'Déclarer un déplacement',
    description: 'Saisir, laisser l’outil calculer la distance et le montant, soumettre.',
    icone: '🚗',
    persona: 'collaborateur',
    etapes: [
      {
        cible: '[data-tour="form-infos"]',
        route: '/declarations/nouvelle',
        titre: 'Où et pour qui',
        texte:
          'La date rattache la déclaration à sa période de paie. Le client se cherche dans le carnet d’adresses : ' +
          'choisissez-le, et son adresse vous est proposée pour le lieu de prestation.',
        consigne: 'Tapez « gar » dans « Client » et choisissez Garage Central.',
      },
      {
        cible: '[data-tour="form-km"]',
        titre: 'La distance, calculée pour vous',
        texte:
          'Un clic calcule la distance routière aller entre les deux adresses (simulée en démo). Vous pouvez la ' +
          'corriger : le valideur verra alors l’écart avec la distance proposée.',
        consigne: 'Cliquez sur « Calculer la distance ».',
      },
      {
        cible: '[data-tour="panneau-calcul"]',
        titre: 'Le montant, en direct',
        texte:
          'Au-delà de 80 km aller-retour, chaque tranche de 120 km vaut une heure payée deux fois le SMIC — au ' +
          'barème du JOUR du déplacement. Le même calcul est rejoué par le serveur à l’enregistrement.',
      },
      {
        cible: '[data-tour="form-nuitee"]',
        titre: 'Une nuit sur place ?',
        texte: 'Chaque nuitée ajoute son forfait. L’hôtel et un commentaire sont facultatifs.',
      },
      {
        cible: '[data-tour="form-actions"]',
        titre: 'Brouillon ou soumission',
        texte:
          'Un brouillon peut rester incomplet. Soumettre contrôle tout (champs, date, période, doublon) puis ' +
          'transmet à votre manager.',
        consigne: 'Complétez les champs signalés et cliquez sur « Soumettre ».',
        avance: { type: 'route' },
      },
      {
        cible: '[data-tour="historique"]',
        titre: 'Chaque geste laisse sa trace',
        texte:
          'Création, soumission, décisions, export : l’historique dit qui a fait quoi, quand, et pourquoi. Il ne ' +
          's’efface jamais.',
      },
      {
        titre: 'Changeons de bureau',
        texte:
          'Votre déclaration attend maintenant Marc MARTIN, votre manager. Le chapitre suivant vous met dans sa peau.',
      },
    ],
  },
  {
    id: 'valider',
    titre: 'Valider en manager',
    description: 'Dans la peau de Marc : valider, corriger, renvoyer ou refuser.',
    icone: '✅',
    persona: 'manager',
    etapes: [
      {
        persona: 'manager',
        titre: 'Vous êtes Marc MARTIN, manager',
        texte:
          'Le rôle a changé dans le bandeau du bas : le menu s’est enrichi de « À valider », et le tableau de bord ' +
          'd’un bloc « Mon équipe ».',
      },
      {
        cible: '[data-tour="bloc-manager"]',
        route: '/',
        titre: 'Votre équipe d’un coup d’œil',
        texte: 'Ce qui attend votre décision, les montants de l’équipe par collaborateur et vos dernières décisions.',
      },
      {
        cible: '[data-tour="validation-liste"]',
        route: '/validation',
        titre: 'Ce qui vous attend',
        texte:
          'Valider d’un clic, ou ouvrir pour décider en connaissance de cause. Renvoyer et refuser exigent un ' +
          'commentaire ; corriger recalcule le montant et garde l’avant/après.',
        consigne: 'Ouvrez une déclaration de Claire en cliquant sur sa ligne.',
        avance: { type: 'route' },
      },
      {
        cible: '[data-tour="decisions"]',
        placement: 'bas',
        titre: 'Décider',
        texte:
          'Avec la validation RH active, votre validation transmet à la RH (niveau 2). Un renvoi rend la main à ' +
          'Claire ; un refus est définitif.',
        consigne: 'Validez-la, ou renvoyez-la avec un commentaire.',
      },
      {
        cible: '[data-tour="historique"]',
        titre: 'Votre décision est tracée',
        texte: 'Elle apparaît dans l’historique, dans le journal des validations, et dans le compteur « À valider » du menu.',
      },
      {
        titre: 'Jamais sa propre déclaration',
        texte:
          'Marc déclare aussi ses déplacements : sans manager au-dessus de lui, c’est la RH qui les valide. On ne ' +
          'valide jamais soi-même — sinon la double validation ne serait qu’un tampon.',
      },
    ],
  },
  {
    id: 'paie',
    titre: 'Valider en RH et exporter la paie',
    description: 'Dans la peau de Rachel : second niveau, contrôles, fichier de paie.',
    icone: '💶',
    persona: 'rh',
    etapes: [
      {
        persona: 'rh',
        titre: 'Vous êtes Rachel PAIE, RH',
        texte: 'Vous tenez le second niveau de validation et l’export vers la paie.',
      },
      {
        cible: '[data-tour="bloc-rh"]',
        route: '/',
        titre: 'Les contrôles avant export',
        texte:
          'Le mois en cours n’est pas prêt : des déclarations attendent une décision, et Inès GARNIER n’a pas de ' +
          'matricule — sa ligne serait rejetée par le logiciel de paie. Ce sont des contrôles BLOQUANTS.',
      },
      {
        cible: '[data-tour="paie-periode"]',
        route: '/paie',
        titre: 'Choisir la période',
        texte: 'Le mois dernier est clôturé et entièrement validé : il est prêt à partir en paie.',
        consigne: 'Choisissez le mois dernier dans la liste.',
      },
      {
        cible: '[data-tour="paie-apercu"]',
        titre: 'Une ligne par salarié',
        texte: 'Les colonnes sont celles du fichier remis au service paie : l’écran et le fichier disent le même chiffre, au centime.',
      },
      {
        cible: '[data-tour="paie-generer"]',
        titre: 'Générer l’export',
        texte:
          'Le fichier part, et ses déclarations passent « Exportée en paie » : elles ne pourront plus être ' +
          'exportées deux fois. En démo, le fichier est un CSV estampillé « données fictives ».',
        consigne: 'Générez l’export du mois dernier.',
      },
      {
        titre: 'Les effets de bord',
        texte:
          'Endossez Claire dans le bandeau : ses déclarations du mois dernier sont désormais « Exportées en paie ». ' +
          'Puis, en administratrice, donnez un matricule à Inès : le contrôle bloquant du mois en cours tombe.',
      },
    ],
  },
  {
    id: 'administrer',
    titre: 'Administrer',
    description: 'Dans la peau d’Alice : comptes, sociétés, barèmes, périodes et réglages.',
    icone: '⚙️',
    persona: 'admin',
    etapes: [
      {
        persona: 'admin',
        cible: '[data-tour="admin-onglets"]',
        route: '/admin',
        titre: 'Vous êtes Alice ADMIN',
        texte: 'L’administration tient les règles du jeu. Chaque modification y est journalisée.',
      },
      {
        cible: '[data-tour="admin-nouvel-utilisateur"]',
        placement: 'bas',
        titre: 'Ouvrir un compte, sans rien retaper',
        texte:
          'Tapez un nom ou une adresse : l’annuaire du groupe (la Supabase, tenue par Youzer) propose le salarié, ' +
          'sa société et son site. Un salarié qui a déjà un compte est signalé.',
        consigne: 'Cliquez sur « Nouvel utilisateur » et tapez « pet » dans le nom.',
        avance: { type: 'modaleFermee' },
      },
      {
        cible: '[data-tour="admin-onglets"]',
        titre: 'Sociétés et sites',
        texte: 'Même principe pour les sociétés et leurs sites : ils viennent de l’annuaire du groupe.',
        consigne: 'Ouvrez l’onglet « Sociétés & sites ».',
        avance: { type: 'apparait', selecteur: '[data-tour="admin-societes"]' },
      },
      {
        cible: '[data-tour="admin-societes"]',
        titre: 'Proposées par l’annuaire',
        texte:
          'En créant une société, choisissez-la dans l’annuaire : son code est proposé, et ses sites peuvent être ' +
          'créés d’un coup. Altonéo Conseil n’est pas encore ouverte ici — essayez.',
      },
      {
        cible: '[data-tour="admin-onglets"]',
        titre: 'Les barèmes',
        texte: 'Le SMIC change ? On ne modifie pas un barème : on en ajoute un, avec sa date d’effet.',
        consigne: 'Ouvrez l’onglet « Barèmes ».',
        avance: { type: 'apparait', selecteur: '[data-tour="admin-baremes"]' },
      },
      {
        cible: '[data-tour="admin-baremes"]',
        titre: 'Versionnés, jamais écrasés',
        texte: 'Un déplacement se paie au barème du jour où il a eu lieu, même validé des mois plus tard.',
      },
      {
        titre: 'Vous avez fait le tour',
        texte:
          'Rejouez un chapitre quand vous voulez depuis « Découverte », ou quittez la démo depuis le bandeau pour ' +
          'retrouver vos vraies données.',
      },
    ],
  },
];

export function chapitreParId(id: string): Chapitre | null {
  return CHAPITRES.find((c) => c.id === id) ?? null;
}
